import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"

import ThemeSwitcher from "../utils/ThemeSwitcher"
import Reveal from "../utils/Reveal"
import Image from "../utils/Image"

import Box from "../atoms/Box"
import Flex from "../atoms/Flex"
import Footer from "../atoms/Footer"
import FlexGrid from "../atoms/FlexGrid"
import StandardPadding from "../atoms/StandardPadding"

import FooterText from "../molecules/FooterText"
import LinkList from "../molecules/LinkList"
import Newsletter from "../molecules/Newsletter"

const FooterDefault = ({
  theme,
  defaultTheme,
  bg,
  bgImage,
  overlayOpacity,
  logo,
  text,
  textPosition,
  linkLists,
  lastSectionStyling,
  hideMorphicBadget,
  morphicBadgeTier,
  badgeBackground,
  mt,
  socialLinks,
  socialLinkStyles,
  useNewsletter,
  newsletter,
  newsletterPosition = "underLogo",
  newsletterAlignment,
  footerWidth,
  ...rest
}) => {
  const isLinkLists = linkLists?.length > 0
  const textRight = textPosition === "right"
  const marginTop =
    lastSectionStyling?.sectionBreak === "slope" ? "-160px" : null
  const pt = lastSectionStyling?.sectionBreak === "slope" ? "157px" : 6
  const pb = lastSectionStyling?.sectionBreak === "slope" ? 9 : 6
  return (
    <ThemeSwitcher theme={theme || defaultTheme}>
      <Footer
        mt={mt ? mt : marginTop}
        zIndex={50}
        bg={bgImage ? null : `background.${bg}`} // this might need to be moved when fixing the overlay opacity slider
        position="relative"
      >
        <Box
          position="absolute"
          zIndex={-1}
          top="0"
          right="0"
          bottom="0"
          left="0"
          bg={`background.${bg}`}
          overflow="hidden"
        >
          {bgImage && (
            <Image
              alternativeText="Background Image"
              {...bgImage}
              style={{
                height: "100%",
                width: "100%",
              }}
            />
          )}
        </Box>
        <Box
          position="absolute"
          zIndex={0}
          top="0"
          right="0"
          bottom="0"
          left="0"
          bg={`background.${bg}`}
          opacity={overlayOpacity || 0}
        />
        <StandardPadding
          px={footerWidth === "fullWidth" ? 5 : { _: 5, lg: 7, xl: 4 }}
          py={null}
          pt={pt}
          pb={pb}
          maxWidth={footerWidth === "fullWidth" ? "100%" : 70}
          {...rest}
        >
          <Flex flexDirection={textRight ? "row-reverse" : ""}>
            <Box
              display={{
                _: "none",
                md: "block",
                lg: "block",
                xl: "block",
              }}
              width={{ md: "45%", lg: linkLists?.length > 2 ? "40%" : "50%" }}
              zIndex={10}
            >
              <Reveal>
                <FooterText
                  logo={logo}
                  text={text}
                  textPosition={textPosition}
                  hideMorphicBadget={hideMorphicBadget}
                  morphicBadgeTier={morphicBadgeTier}
                  badgeBackground={badgeBackground}
                  socialLinks={socialLinks}
                  socialLinkStyles={socialLinkStyles}
                  align={textRight ? "right" : "left"}
                  pr={textRight ? 0 : 5}
                  pl={textRight ? 5 : 0}
                  newsletterJustify={textRight ? "flex-end" : "start"}
                  newsletterPosition={newsletterPosition}
                  useNewsletter={useNewsletter}
                  newsletter={newsletter}
                />
              </Reveal>
            </Box>
            {isLinkLists && (
              <Box
                width={{
                  _: "100%",
                  md: "50%",
                  lg: linkLists?.length > 2 ? "60%" : "50%",
                }}
                zIndex={10}
              >
                <FlexGrid
                  columns={{
                    _: Math.min(2, linkLists.length),
                    sm:
                      useNewsletter && newsletterPosition === "opposite"
                        ? 2
                        : Math.min(
                            linkLists.length % 2 === 0 ? 2 : 3, // if even, stay at 2. Otherwise 3
                            linkLists.length
                          ),
                    md: Math.min(3, linkLists.length),
                    lg:
                      useNewsletter && newsletterPosition === "opposite"
                        ? 3
                        : Math.min(4, linkLists.length),
                  }}
                  gutter={5}
                >
                  {linkLists.map((linkList, i) => (
                    <LinkList variant="footerLink" {...linkList} key={i} />
                  ))}
                </FlexGrid>
                {useNewsletter &&
                  (newsletterPosition === "underLinks" ||
                    newsletterPosition === "opposite") && (
                    <Box
                      mb={4}
                      mt={5}
                      display={
                        // if position is set to oppisite and mobile breakpoint set to "underLinks"
                        newsletterPosition === "opposite"
                          ? {
                              _: "block",
                              sm: "block",
                              md: "block",
                              lg: "none",
                            }
                          : "block"
                      }
                    >
                      <Newsletter
                        integration={newsletter?.integration}
                        formStyle={newsletter?.formStyle}
                        formWidth={newsletter?.formWidth}
                        title={newsletter?.title}
                        titleAlignment={newsletter?.titleAlignment}
                        boxShadow={newsletter?.boxShadow}
                        borderRadius={newsletter?.borderRadius}
                        justify={"start"}
                      />
                    </Box>
                  )}
              </Box>
            )}
            {useNewsletter && newsletterPosition === "opposite" && (
              <Box
                mb={4}
                display={
                  linkLists.length < 1
                    ? "flex"
                    : {
                        // only display "opposite" for non-mobile devices
                        _: "none",
                        sm: "none",
                        md: "none",
                        lg: "flex",
                      }
                }
                flexDirection={"column"}
                justifyContent={
                  newsletterAlignment === "top"
                    ? "start"
                    : newsletterAlignment === "center"
                    ? newsletterAlignment
                    : newsletterAlignment === "bottom"
                    ? "flex-end"
                    : "start"
                }
                mr={textPosition === "right" ? 4 : 0}
              >
                <Newsletter
                  integration={newsletter?.integration}
                  formStyle={newsletter?.formStyle}
                  formWidth={newsletter?.formWidth}
                  title={newsletter?.title}
                  titleAlignment={newsletter?.titleAlignment}
                  boxShadow={newsletter?.boxShadow}
                  borderRadius={newsletter?.borderRadius}
                />
              </Box>
            )}
          </Flex>
          <Box
            display={{
              _: "block",
              md: "none",
              lg: "none",
              xl: "none",
            }}
            zIndex={10}
          >
            <Reveal>
              <FooterText
                logo={logo}
                text={text}
                textPosition={textPosition}
                align={textRight ? "right" : "left"}
                mt={isLinkLists ? 6 : 0}
                hideMorphicBadget={hideMorphicBadget}
                morphicBadgeTier={morphicBadgeTier}
                socialLinks={socialLinks}
                socialLinkStyles={socialLinkStyles}
                newsletterPosition={newsletterPosition}
                useNewsletter={useNewsletter}
                newsletter={newsletter}
                newsletterJustify={"start"}
              />
            </Reveal>
          </Box>
        </StandardPadding>
      </Footer>
    </ThemeSwitcher>
  )
}

export default FooterDefault

FooterDefault.strapiProps = {
  theme: PropTypes.oneOf(["default", "light", "dark", "highlight"]),
  defaultTheme: PropTypes.oneOf(["default", "light", "dark", "highlight"])
    .isRequired,
  bg: PropTypes.number.isRequired,
  logo: PropTypes.object,
  text: PropTypes.string,
  textPosition: PropTypes.oneOf(["left", "right"]).isRequired,
  linkLists: PropTypes.arrayOf(PropTypes.shape(LinkList.strapiProps)),
  morphicBadgeTier: PropTypes.oneOf(["morphic", "enterprise"]),
  badgeBackground: PropTypes.oneOf(["white", "transparent"]),
}

FooterDefault.propTypes = {
  ...FooterDefault.strapiProps,
}

export const query = graphql`
  fragment FooterDefault on STRAPI__COMPONENT_FOOTERS_DEFAULT {
    defaultTheme: theme
    bg
    logo {
      ...ImageWith176pxWidth
    }
    bgImage {
      ...ImageWithConstrainedLayout
    }
    overlayOpacity
    socialLinks {
      ...SocialLink
    }
    socialLinkStyles {
      ...SocialLinkStyles
    }
    newsletter {
      ...Newsletter
    }
    useNewsletter
    newsletterPosition
    newsletterAlignment
    text
    textPosition
    linkLists {
      ...LinkList
    }
    footerWidth
    hideMorphicBadget
    morphicBadgeTier
    badgeBackground
  }
`
